import {
  Alert, Box, Button, FormField, Header, Input, Multiselect, Select,
  SpaceBetween, Table,
} from '@cloudscape-design/components';
import { FmCatalog } from '../fmBundles';
import { Errors, FmWizardState, SelectedModel, SHORT_NAME_MAX, uniqueShortName } from './types';

interface Props {
  state: FmWizardState;
  errors: Errors;
  catalog: FmCatalog | null;
  catalogLoading: boolean;
  refreshing: boolean;
  vendor: string;
  onVendorChange: (v: string) => void;
  onRefreshCatalog: () => void;
  patch: (p: Partial<FmWizardState>) => void;
}

export default function ModelsStep({
  state, errors, catalog, catalogLoading, refreshing, vendor,
  onVendorChange, onRefreshCatalog, patch,
}: Props) {
  const providers = catalog?.providers ?? [];
  const vendorModels = providers.find((p) => p.provider === vendor)?.models ?? [];
  const selectedIds = new Set(state.models.map((m) => m.modelId));

  const onMultiselectChange = (chosen: readonly { value?: string }[]) => {
    const chosenIds = chosen.map((o) => o.value!);
    // Remove this vendor's deselected models, keep others.
    let next: SelectedModel[] = state.models.filter(
      (m) => m.provider !== vendor || chosenIds.includes(m.modelId),
    );
    for (const id of chosenIds) {
      if (selectedIds.has(id)) continue;
      const model = vendorModels.find((m) => m.modelId === id);
      if (!model) continue;
      const shortName = uniqueShortName(model.modelName, next.map((m) => m.shortName));
      next = [...next, { modelId: id, modelName: model.modelName, provider: vendor, shortName }];
    }
    patch({ models: next });
  };

  const setShortName = (index: number, value: string) =>
    patch({ models: state.models.map((m, i) => (i === index ? { ...m, shortName: value } : m)) });

  const removeModel = (index: number) =>
    patch({ models: state.models.filter((_, i) => i !== index) });

  const vendorSelection = vendorModels
    .filter((m) => selectedIds.has(m.modelId))
    .map((m) => ({ label: m.modelName, value: m.modelId }));

  return (
    <SpaceBetween size="m">
      <Header
        variant="h3"
        actions={<Button iconName="refresh" loading={refreshing} onClick={onRefreshCatalog}>Refresh catalog</Button>}
      >
        Choose models
      </Header>

      {!catalogLoading && providers.length === 0 && (
        <Alert type="info">
          The model catalog is empty. Use "Refresh catalog" to sync models from Bedrock (requires the catalog-sync account credentials in Settings).
        </Alert>
      )}

      <FormField label="Vendor" description="Pick a vendor to list its available models.">
        <Select
          selectedOption={vendor ? { label: vendor, value: vendor } : null}
          onChange={({ detail }) => onVendorChange(detail.selectedOption.value!)}
          options={providers.map((p) => ({ label: p.provider, value: p.provider }))}
          placeholder="Select a vendor"
          statusType={catalogLoading ? 'loading' : 'finished'}
          loadingText="Loading catalog"
          disabled={providers.length === 0}
        />
      </FormField>

      <FormField label="Models" description="Select one or more models for this vendor. Selections accumulate across vendors.">
        <Multiselect
          selectedOptions={vendorSelection}
          onChange={({ detail }) => onMultiselectChange(detail.selectedOptions)}
          options={vendorModels.map((m) => ({ label: m.modelName, value: m.modelId }))}
          placeholder="Select models"
          disabled={!vendor}
          filteringType="auto"
        />
      </FormField>

      <Table
        variant="container"
        header={<Header counter={`(${state.models.length})`}>Selected models</Header>}
        items={state.models.map((m, i) => ({ ...m, index: i }))}
        trackBy="modelId"
        columnDefinitions={[
          { id: 'modelName', header: 'Model', cell: (i) => i.modelName },
          { id: 'provider', header: 'Vendor', cell: (i) => i.provider },
          {
            id: 'shortName',
            header: 'Short name',
            cell: (i) => (
              <FormField
                errorText={errors[`models.${i.index}.shortName`]}
                constraintText={`${i.shortName.length}/${SHORT_NAME_MAX}`}
              >
                <Input value={i.shortName} onChange={({ detail }) => setShortName(i.index, detail.value)} />
              </FormField>
            ),
          },
          {
            id: 'remove',
            header: '',
            cell: (i) => <Button variant="icon" iconName="remove" ariaLabel="Remove model" onClick={() => removeModel(i.index)} />,
          },
        ]}
        empty={<Box textAlign="center" color="text-body-secondary">No models selected</Box>}
      />
      {errors.models && <Box color="text-status-error">{errors.models}</Box>}
    </SpaceBetween>
  );
}
